import React, {ReactElement, useCallback, useMemo} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {Category, CategoryInterface} from 'shared/components/Product/Category';
import {useItemColumn} from 'shared/components/Product/ItemColumn';
import styled from 'styled-components';
import {useProductContext} from 'contexts';
import {useIsMobileLandscape} from 'shared/helpers/DeviceSize';
import {FAVOURITE_CATEGORY_NAME} from 'hooks/QuickProduct/ProductSearch';
import {SHOW_ALL_BUTTON} from 'hooks/Product/Product';

const isTrailingCatalogTile = (category: CategoryInterface) =>
    category.name === FAVOURITE_CATEGORY_NAME ||
    category.name === SHOW_ALL_BUTTON;

type Category = {
    name: string;
    imageUrl: string;
    id: number;
    styleName: string;
    styleImage: string;
    noImage: boolean;
    action: () => void;
    isSubCategory: boolean;
    subStyles: {
        subStyleId: number;
        subStyleName: string;
        subStyleImage: string;
    }[];
};

type CategoriesType = {
    isProduct: boolean;
    categories: CategoryInterface[];
    loader: boolean;
};

export const Categories = ({
    isProduct,
    categories,
    loader,
}: CategoriesType): ReactElement => {
    const isMobileLandscape = useIsMobileLandscape();
    const {roomId} = useParams();
    const [searchParams] = useSearchParams();
    const category = searchParams.get('category') || false;
    const {loaders} = useItemColumn(loader);
    const {isMiniBrowser} = useProductContext();

    const navigate = useNavigate();

    const {productCategories, trailingCategories} = useMemo(() => {
        const productCategories: CategoryInterface[] = [];
        const trailingCategories: CategoryInterface[] = [];

        (categories ?? []).forEach((catalogCategory) => {
            if (isTrailingCatalogTile(catalogCategory)) {
                trailingCategories.push(catalogCategory);
            } else {
                productCategories.push(catalogCategory);
            }
        });

        return {productCategories, trailingCategories};
    }, [categories]);

    const categoryClickHandler = useCallback(
        ({id, isSubCategory}: CategoryInterface) => {
            const searchParams = new URLSearchParams(location.search);

            if (roomId) {
                if (isSubCategory) {
                    searchParams.set('category', String(category));
                    searchParams.set('subCategory', String(id));
                } else {
                    searchParams.set('category', String(id));
                    searchParams.delete('subCategory');
                }
            } else {
                searchParams.set('category', String(id));
            }

            void navigate({
                pathname: location.pathname,
                search: searchParams.toString(),
            });
        },
        [roomId, category, location.search, location.pathname, navigate]
    );

    const renderCategoryTile = (catalogCategory: CategoryInterface) => (
        <Category
            isMiniBrowser={isMiniBrowser}
            category={catalogCategory}
            categoryClickHandler={categoryClickHandler}
            key={`${catalogCategory.id}-${catalogCategory.name}`}
            isProduct={isProduct}
        />
    );

    return (
        <>
            {loader ? (
                <LoaderContainer
                    $miniBrowser={isMiniBrowser ?? false}
                    className="hardwareItems">
                    {loaders}
                </LoaderContainer>
            ) : null}

            <CategoryContainer
                className="hardwareCategories"
                $fixed={
                    categories
                        ? isMiniBrowser
                            ? categories.length <= 1
                            : categories.length < 5
                        : false
                }
                $miniBrowser={isMiniBrowser}
                $mobileLandscape={isMobileLandscape}>
                {!loader ? productCategories.map(renderCategoryTile) : null}
                {!loader ? trailingCategories.map(renderCategoryTile) : null}
            </CategoryContainer>
        </>
    );
};

export const CategoryContainer = styled.div<{
    $fixed?: boolean;
    $marginBottom?: boolean;
    $miniBrowser?: boolean;
    $mobileLandscape?: boolean;
}>`
    display: grid;
    align-items: stretch;
    grid-gap: 15px;

    ${({$marginBottom = false}) => {
        if ($marginBottom) {
            return `
                margin-bottom: 170px !important;
            `;
        }
    }}

    grid-template-columns: repeat(auto-fill, minmax(${({$miniBrowser}) =>
        $miniBrowser ? '149px' : '220px'}, 1fr));

    @media (max-width: 768px) {
        grid-template-columns: repeat(auto-fit, minmax(149px, 1fr));
    }

    ${({$mobileLandscape}) => {
        if ($mobileLandscape) {
            return `grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));`;
        }
    }}
`;

const LoaderContainer = styled.div<{$miniBrowser: boolean}>`
    display: flex;
    gap: 15px;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    flex-wrap: wrap;

    > div {
        ${({$miniBrowser}) => ($miniBrowser ? 'width: 150px' : 'initial')};
    }
`;
